// ASYM CLAMP RELIEF (G-1-01515, 2026-10-15 — TGCLOUD-1908 QA round 6, Tim's ruling:
// "a free spouse steps away").
//
// A hub's asym-anchored union (crossingRefine's park / HUB-ASYM ANCHOR / IN-LAW
// writers, family.asymJunctionsV3) follows its children's midrange along the bar,
// clamped to a band ASYM_JUNCTION_PARENT_CLEARANCE symbol widths from each partner.
// When the midrange lies inside that band beside a BARE married-in spouse, or past
// it, the clamp parks the junction at the band's edge: the drop lands off the
// children and hugs the spouse (01515's 33, with 36 and 37 dead under it, and 18,
// with 17/15/16's midrange half a unit past it — "bunched up with the drop").
//
// The mirror of the park pass, which pulls a FLUNG free spouse in to the standard
// partner→drop offset: here the spouse is too close, so it steps OUTWARD along its
// row, away from the hub, into the room it has, until the child column clears it by
// that same offset (PARK_OFF, a plain couple's). Nothing but its own bar ties a bare
// spouse (one union, no parents drawn, every child shared with the hub) to its
// column, so only the hub-side bar segment lengthens; the junction then lands on the
// children (buildRenderModel's band holds it) and the drop is straight.
//
// It runs on the FINISHED layout — after the candidate ladder has chosen the winner,
// like decideUnflankedSpouseLifts — never inside the refine: there it improved a
// candidate's dog-leg score and changed which layout won (G-1-01401 lost a straight
// drop). Crossing-gated on the real render model; the row order cannot change (the
// step stops MIN short of the next node) and no node can come under the bar (the
// step covers only the room beside the spouse). The 1908 column solve cannot make
// this move: its people never go left of their floor position, and at the floor
// every gap is already at its minimum.

import { config } from './config.js';
import { detectAllSegmentIntersections } from './lineIntersection.js';

const PARK_OFF = 0.5; // layoutV2's COUPLE_HALF_GAP — the normal partner→drop offset
const pairKey = (a, b) => (a < b ? `${a}:${b}` : `${b}:${a}`);

/**
 * Step every bare spouse whose asym junction is clamped against it. Mutates the
 * family's virtualX (and re-runs convertToCoordinates) and returns true when
 * anything moved.
 */
export function relieveAsymClamps(family, customPositions, buildRenderModel) {
  if (config.ENABLE_ASYM_CLAMP_RELIEF === false) return false;
  const persons = family && family.persons;
  if (!persons || !persons.length) return false;
  const marks = family.asymJunctionsV3;
  if (!marks || !marks.size) return false;
  const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
  const minOffVX =
    ((config.ASYM_JUNCTION_PARENT_CLEARANCE ?? 0.75) *
      (config.SYMBOL_SIZE || 155)) /
    (config.NODE_GAP || 800);
  const pinned = (q) =>
    !!(customPositions && q.uid != null && customPositions.has(q.uid));

  // The marked unions, grouped as the render groups them (layout.js sharedChildren).
  const unions = [];
  const seen = new Set();
  for (const p of persons) {
    for (const sp of p.spouses || []) {
      const key = pairKey(p.pid, sp.pid);
      if (seen.has(key)) continue;
      seen.add(key);
      if (!marks.has(key)) continue;
      const kids = (p.children || []).filter(
        (c) => c.motherId === sp.pid || c.fatherId === sp.pid
      );
      if (!kids.length) continue;
      unions.push({ a: p, b: sp, kids, key });
    }
  }
  if (!unions.length) return false;
  unions.sort((u, v) => (u.key < v.key ? -1 : u.key > v.key ? 1 : 0));

  let converted = false;
  const crossOf = () => {
    family.convertToCoordinates();
    converted = true;
    const { edges, nodesById } = buildRenderModel(
      family,
      customPositions,
      true
    );
    const m = detectAllSegmentIntersections(edges, nodesById);
    return m ? m.size ?? 0 : 0;
  };
  let bestCross = null;
  let moved = false;
  for (const { a, b, kids } of unions) {
    if ((a.virtualY ?? 0) !== (b.virtualY ?? 0)) continue;
    const spanVX = Math.abs(a.virtualX - b.virtualX);
    if (spanVX <= 2 * minOffVX) continue; // too short for a band: midpoint junction
    const xs = kids.map((c) => c.virtualX);
    const mid = (Math.min(...xs) + Math.max(...xs)) / 2;
    const left = a.virtualX <= b.virtualX ? a : b;
    const right = left === a ? b : a;
    let S = null;
    let dir = 0;
    if (mid < left.virtualX + minOffVX - 1e-9) {
      S = left;
      dir = -1;
    } else if (mid > right.virtualX - minOffVX + 1e-9) {
      S = right;
      dir = 1;
    } else continue; // the junction already sits on the children
    const hub = S === a ? b : a;
    // Bare: no spouse but the hub (a one-sided fixture lists none at all), no
    // parents drawn, no child outside this union, not pinned.
    if ((S.spouses || []).some((q) => q.pid !== hub.pid)) continue;
    if (S.hasParents) continue;
    if ((S.children || []).some((c) => !kids.includes(c))) continue;
    if (pinned(S)) continue;
    // Not when the hub has another spouse on S's side: that spouse's union
    // anchors its staircase off S (extraSpouseInfo), so S's column is not its own.
    if (
      (hub.spouses || []).some(
        (q) =>
          q.pid !== S.pid &&
          (q.virtualY ?? 0) === (S.virtualY ?? 0) &&
          (q.virtualX - hub.virtualX) * (S.virtualX - hub.virtualX) > 0
      )
    )
      continue;
    // (mid − S)·dir is minus the clearance the child column has from S today.
    const want = PARK_OFF + (mid - S.virtualX) * dir;
    if (want <= 1e-9) continue;
    let room = Infinity;
    for (const q of persons) {
      if (q.pid === S.pid) continue;
      if ((q.virtualY ?? 0) !== (S.virtualY ?? 0)) continue;
      const d = (q.virtualX - S.virtualX) * dir;
      if (d > 0) room = Math.min(room, d - MIN);
    }
    const step = Math.min(want, room);
    if (step <= 1e-9) continue;
    if (bestCross == null) bestCross = crossOf();
    const prev = S.virtualX;
    S.virtualX += dir * step;
    const c = crossOf();
    if (c <= bestCross) {
      bestCross = c;
      moved = true;
    } else {
      S.virtualX = prev;
    }
  }
  if (converted) family.convertToCoordinates(); // pixels back in step with virtualX
  return moved;
}
